/**
 * Every file the OCR path loads at run time, and where the site serves it.
 *
 * ONE LIST, TWO READERS: `build/build.mjs` copies each `from` to `web/static/vendor/<to>`, and the
 * page builds its URLs from the same `to`. A path that drifts on one side and not the other is a
 * 404 on a visitor's first recognition, which no other test would see.
 *
 * Everything is self-hosted. tesseract.js defaults to jsDelivr for its worker, its engine and its
 * language data; each of those defaults is replaced here, and the CSP allows no other origin.
 *
 * NONE OF THESE IS LOADED ON AN ORDINARY PAGE VIEW. The page bundle holds only the URL strings;
 * the first image-subtitle drop on an OCR page is what fetches them (web/static/ocr/src/ocr.ts).
 *
 * Measured 2026-10-17, raw bytes as served:
 *
 *   tesseract engine, one of three by CPU   3,896,484 – 3,905,767   (1.45 MB gzipped)
 *   English model                           2,952,873               (already gzip)
 *   tesseract worker                          111,307
 *   libbitsub decoder (WASM)                  163,349
 *
 * About 7.1 MB on first use, of which the browser downloads ~4.6 MB when the server compresses
 * the engine. The model is cached in IndexedDB by tesseract.js (`cachePath` below), so a second
 * visit downloads only the engine, which the HTTP cache usually holds too.
 *
 * VERSIONED BY DIRECTORY. tesseract.js appends file names to `corePath` and `langPath`, so those
 * URLs cannot carry the `?v=` every other asset does. Each directory is named for its upstream
 * version instead, so a new engine can never be served beside an old worker.
 */

/** Where the build writes these, relative to `web/static/`, and the URL prefix that serves it. */
export const VENDOR_URL = '/static/vendor';

export const TESSERACT_VERSION = '7.0.0';
export const TESSDATA_VARIANT = '4.0.0_best_int';
export const LIBBITSUB_VERSION = '1.13.0';

const TESS_DIR = `ocr/tesseract-${TESSERACT_VERSION}`;
const CORE_DIR = `${TESS_DIR}/core`;
const LANG_DIR = `ocr/tessdata-eng-${TESSDATA_VARIANT}`;
const BITSUB_DIR = `ocr/libbitsub-${LIBBITSUB_VERSION}`;

export interface VendorFile {
  /** Relative to the repository root. */
  readonly from: string;
  /** Relative to `web/static/vendor/`. */
  readonly to: string;
  /** SHA-256 the build checks, for files that do not come from the lockfile. */
  readonly sha256?: string;
}

/**
 * The three LSTM-only engines. tesseract.js picks one by what the CPU supports (relaxed SIMD,
 * SIMD, neither), so all three are served and each visitor downloads one. The `.wasm.js` builds
 * carry the WASM inside the script, which is what `importScripts` in the worker expects; the
 * legacy (non-LSTM) engines are not shipped because the site runs `OEM.LSTM_ONLY`.
 */
const CORES = [
  'tesseract-core-lstm.wasm.js',
  'tesseract-core-simd-lstm.wasm.js',
  'tesseract-core-relaxedsimd-lstm.wasm.js',
];

export const OCR_VENDOR_FILES: readonly VendorFile[] = [
  { from: 'node_modules/tesseract.js/dist/worker.min.js', to: `${TESS_DIR}/worker.min.js` },
  ...CORES.map((name) => ({
    from: `node_modules/tesseract.js-core/${name}`,
    to: `${CORE_DIR}/${name}`,
  })),
  {
    from: `src/vendor/tessdata-eng-${TESSDATA_VARIANT}/eng.traineddata.gz`,
    to: `${LANG_DIR}/eng.traineddata.gz`,
    sha256: '45b4cb346724ac1774f1c36f42f182b887bcdb28ebe63e6fff90ac41f3fcff91',
  },
  { from: 'node_modules/libbitsub/pkg/libbitsub_bg.wasm', to: `${BITSUB_DIR}/libbitsub_bg.wasm` },
];

/** The URLs the OCR page hands to tesseract.js and to the decode worker. */
export interface OcrAssetUrls {
  /** tesseract's own worker script, cache-busted with the build id. */
  readonly workerPath: string;
  /** A DIRECTORY: tesseract.js appends the engine's file name. */
  readonly corePath: string;
  /** A DIRECTORY: tesseract.js appends `eng.traineddata.gz`. */
  readonly langPath: string;
  /** The IndexedDB key prefix for the model, so a new model is fetched rather than the old reused. */
  readonly cachePath: string;
  /** libbitsub's WASM, fetched by the decode worker. */
  readonly libbitsubWasm: string;
  /** The decode worker bundle (build/entrypoints.mjs convention). */
  readonly decodeWorker: string;
}

export function ocrAssetUrls(buildId: string | null): OcrAssetUrls {
  const v = buildId ? `?v=${encodeURIComponent(buildId)}` : '';
  return {
    workerPath: `${VENDOR_URL}/${TESS_DIR}/worker.min.js${v}`,
    corePath: `${VENDOR_URL}/${CORE_DIR}`,
    langPath: `${VENDOR_URL}/${LANG_DIR}`,
    cachePath: `tessdata-eng-${TESSDATA_VARIANT}`,
    libbitsubWasm: `${VENDOR_URL}/${BITSUB_DIR}/libbitsub_bg.wasm${v}`,
    decodeWorker: `/static/build/ocr/decode-worker.js${v}`,
  };
}
